import {User} from '@microsoft/microsoft-graph-types'
import {makeAutoObservable, reaction, runInAction} from 'mobx'
import {Logger, UserAgentApplication} from 'msal'
import config from 'src/config'
import DBUser from 'src/entities/User'
import {MainStore} from 'src/store/MainStore'
import {readGateLoginHint} from 'src/utils/gateLoginHint'
import popupWindow from 'src/utils/popupWindow'

export class LoginStore {
  readonly msal: UserAgentApplication

  // True until init() has resolved the session - the route guards wait for it instead of
  // bouncing to /login and showing the sign-in button while a silent sign-in is running.
  isInitializing: boolean = true

  isMsalAuth: boolean = false

  isAuth: boolean = false

  roles: string[] = []

  isLoggingIn: boolean = false

  isFetchingUser: boolean = false

  user: User | null = null

  currentUser: DBUser | null = null

  loginError: string = ''

  tokenError: string = ''

  shouldLogout: boolean = false

  loginMessage: string = ''

  constructor(readonly owner: MainStore) {
    makeAutoObservable(this)
    this.msal = new UserAgentApplication({
      auth: {
        redirectUri: config.AADRedirectUrl,
        postLogoutRedirectUri: window.location.href,
        ...config.AADConfig
      },
      cache: {cacheLocation: 'localStorage'},
      system: {
        logger: new Logger((level: any, message: any) => {
          if (!config.isProduction) console.log('[msal]', level, message)
        })
      }
    })

    reaction(
      () => this.isMsalAuth,
      async isMsalAuth => {
        if (isMsalAuth && (await this.getAccessToken(this.scope.graph))) {
          if (!this.currentUser) this.getCurrentUser()
        }
      }
    )

    reaction(
      () => this.isAuth,
      async isAuth => {
        if (isAuth) this.getUserProfile()
      }
    )
  }

  get isAccessDenied() {
    return !this.isAuth && this.loginMessage !== ''
  }

  init() {
    runInAction(() => {
      this.loginMessage = ''
      this.isInitializing = true
    })
    const isTestMode = localStorage.getItem('test-mode') === 'true'
    const hasSession = (this.msal && !!this.msal.getAccount()) || isTestMode

    const resume = (resumed: boolean) =>
      runInAction(() => {
        this.isMsalAuth = resumed
        this.isFetchingUser = resumed
        this.isInitializing = false
      })

    if (hasSession) {
      resume(true)
      return
    }
    // Edge gate (netlify/edge-functions/gate.ts): the visitor already signed in to Microsoft
    // at the gate, which leaves the address in the readable hub_gate_user cookie. With no
    // session of our own, a silent sign-in for that account comes first, so the user signs
    // in once (at the gate) and not again on the login page. Without the cookie (gate off)
    // nothing changes.
    this.signInFromGateHint().then(resume)
  }

  // A silent sign-in (hidden iframe, prompt=none) for the account the edge gate signed in,
  // with the gate's address as the login hint. Any failure is only logged - the login page
  // then asks, as before.
  private async signInFromGateHint(): Promise<boolean> {
    const loginHint = readGateLoginHint()
    if (!loginHint) return false
    try {
      const result = await this.msal.ssoSilent({
        scopes: this.scope.graph,
        loginHint,
        state: this.requestState
      })
      if (!result.account) return false
      console.info(
        '[auth] signed in silently for the account from the edge gate'
      )
      return true
    } catch (err: any) {
      console.info(
        '[auth] silent sign-in for the gate account did not succeed:',
        err?.errorCode,
        err?.errorMessage || err?.message
      )
      return false
    }
  }

  get isManager() {
    return this.currentUser?.roleId === 2
  }

  async login() {
    runInAction(() => {
      this.isLoggingIn = true
      this.loginError = ''
      this.loginMessage = ''
    })
    try {
      // Login via popup
      await this.msal.loginPopup({
        scopes: this.scope.graph,
        prompt: 'select_account',
        state: this.requestState
      })

      runInAction(() => {
        this.isAuth = true
        this.isLoggingIn = false
        this.shouldLogout = false
        this.isMsalAuth = true
        this.isFetchingUser = true
        this.isFetchingUser = true
        this.loginMessage = ''
      })
    } catch (err: any) {
      runInAction(() => {
        this.isLoggingIn = false
        this.isAuth = false
        this.roles = []
        this.isMsalAuth = false
        this.loginError = err.errorMessage
      })
    }
  }

  logout = () => {
    if (window.opener && window.opener !== window) return this.msal.logout()
    const logoutWin = popupWindow('/logout', '', 500, 600)
    if (logoutWin)
      logoutWin.onbeforeunload = () =>
        runInAction(() => {
          this.isAuth = false
          this.roles = []
          this.isMsalAuth = false
          this.user = null
          this.currentUser = null
          this.loginMessage = ''
        })
  }

  fetchWithUser = async (
    endpoint: RequestInfo,
    options: RequestInit = {},
    scopes: string[] = this.scope.api
  ): Promise<Response> => {
    if (localStorage.getItem('test-mode') === 'true') {
      const url = endpoint.toString()
      if (url.includes('/Institutions') && !url.includes('/Institutions/')) {
        return new Response(
          JSON.stringify([
            {
              id: 1,
              name: 'Test Institution',
              typeId: 1,
              statusId: 1,
              symbol: '123'
            }
          ]),
          {
            status: 200,
            statusText: 'OK',
            headers: {'Content-Type': 'application/json'}
          }
        )
      }
      if (url.includes('/Students/Institution/')) {
        return new Response(
          JSON.stringify([
            {
              id: 1,
              firstName: 'Student',
              lastName: 'One',
              indificationNo: '111111111',
              updatedAt: new Date().toISOString()
            }
          ]),
          {
            status: 200,
            statusText: 'OK',
            headers: {'Content-Type': 'application/json'}
          }
        )
      }
      if (url.includes('/CodeTables')) {
        return new Response(JSON.stringify([]), {status: 200})
      }
      return new Response(JSON.stringify({}), {status: 200})
    }

    const accessToken = await this.getAccessToken(scopes)
    const headers = {
      ...options.headers,
      Authorization: `Bearer ${accessToken}`
    }
    const mergedOpts = {...options, headers}
    return fetch(endpoint, mergedOpts).then(response => {
      if (!response.ok) {
        return Promise.reject(response)
      }
      return response
    })
  }

  getUserProfile = async () => {
    runInAction(() => (this.isFetchingUser = true))
    const res = await this.fetchWithUser(
      'https://graph.microsoft.com/v1.0/me',
      {},
      this.scope.graph
    )
    const {error, ...user} = await res.json()
    if (error) runInAction(() => (this.isFetchingUser = false))
    runInAction(() => {
      this.user = user
      this.isFetchingUser = false
    })
  }

  getCurrentUser = () => {
    if (localStorage.getItem('test-mode') === 'true') {
      runInAction(() => {
        this.isAuth = true
        this.currentUser = new DBUser({
          id: 1,
          fullName: 'Test User',
          roleId: 2,
          email: 'test@example.com'
        } as any)
        this.isLoggingIn = false
        this.shouldLogout = false
        this.isMsalAuth = true
        this.isFetchingUser = false
        this.loginMessage = ''
      })
      return
    }
    this.fetchWithUser(config.apiUrl + '/Users/current')
      .then(response => response !== null && response.json())
      .then((data: DBUser) => {
        runInAction(() => {
          this.isAuth = true
          this.currentUser = new DBUser(data)
          this.isLoggingIn = false
          this.shouldLogout = false
          this.isMsalAuth = true
          this.isFetchingUser = false
          this.loginMessage = ''
        })
      })
      .catch(() => {
        runInAction(() => {
          this.shouldLogout = true
          this.isAuth = false
          this.roles = []
          this.isMsalAuth = false
          this.isLoggingIn = false
          this.loginMessage =
            'User does not exist in the system, please contact your administrator'
        })
      })
  }

  scope = {
    graph: ['user.read'],
    api: ['api://' + config.AADConfig.clientId + '/user_impersonation']
  }

  requestState = JSON.stringify({
    redirectUrl:
      typeof window !== 'undefined' ? window.location.origin + '/msal.html' : ''
  })

  clearCache = () => {
    localStorage.clear()
  }

  isInteractionRequired = (error: Error): boolean => {
    if (!error.message || error.message.length <= 0) {
      return false
    }
    return (
      error.message.indexOf('consent_required') > -1 ||
      error.message.indexOf('interaction_required') > -1 ||
      error.message.indexOf('login_required') > -1
    )
  }

  async getAccessToken(scopes: string[]): Promise<string> {
    if (localStorage.getItem('test-mode') === 'true') {
      return 'mock-token'
    }
    const tokenrequest = {
      scopes: scopes,
      state: this.requestState
    }
    try {
      // Get the access token  silently
      // If the cache contains a non-expired token, this function
      // will just return the cached token. Otherwise, it will
      // make a request to the Azure OAuth endpoint to get a token
      var silentResult = await this.msal.acquireTokenSilent(tokenrequest)
      return silentResult.accessToken
    } catch (err: any) {
      // If a silent request fails, it may be because the user needs
      // to login or grant consent to one or more of the requested scopes
      if (
        err.name === 'InteractionRequiredAuthError' ||
        this.isInteractionRequired(err)
      ) {
        try {
          var interactiveResult = await this.msal.acquireTokenPopup(
            tokenrequest
          )
          return interactiveResult.accessToken
        } catch {
          runInAction(() => {
            this.shouldLogout = true
            this.isAuth = false
            this.roles = []
            this.isMsalAuth = false
            this.isLoggingIn = false
            this.tokenError = err
          })
          return ''
        }
      } else {
        runInAction(() => {
          this.shouldLogout = true
          this.isAuth = false
          this.roles = []
          this.isMsalAuth = false
          this.isLoggingIn = false
          this.tokenError = err
        })
        return ''
      }
    }
  }

  async getAuthorizationHeader() {
    const accessToken = await this.getAccessToken(this.scope.api)
    return {Authorization: `Bearer ${accessToken}`}
  }
}
